import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/Toast";

export interface EntityActionOptions {
  /** Runs after a successful call — this is where a form / window closes itself. */
  onSuccess?: () => void;
  /** The "done" toast. Defaults to "Сохранено"; `false` for an action that shows
   * its own confirmation. */
  successMessage?: string | false;
  /** Set when the call site shows `action.error` itself, inside the form or window
   * (an `<ErrorNote>` next to the buttons) — the error toast is then skipped so
   * the same message isn't shown twice. Without it a failure raises an error toast. */
  inlineError?: boolean;
}

/**
 * Runs an async action (a save / delete / restore / write call) the way a
 * normal website's submit button behaves:
 *
 *  - a second call while one is in flight is ignored (double-click safe — the
 *    guard is a ref, so it holds even before React re-renders the button as
 *    disabled); `isPending` drives the button's loading state;
 *  - on success the given query keys are refetched, `onSuccess` runs (close the
 *    window) and a short "Сохранено" toast appears;
 *  - on failure nothing is closed or cleared — the caller's local form state is
 *    untouched — and the reason is shown (inline via `inlineError`, else a toast).
 */
export function useEntityAction(
  invalidate: unknown[][],
  opts: EntityActionOptions = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  const inFlight = useRef(false);

  const mutation = useMutation({
    mutationFn: (call: () => Promise<unknown>) => call(),
    onSuccess: () => {
      for (const key of invalidate) qc.invalidateQueries({ queryKey: key });
      opts.onSuccess?.();
      if (opts.successMessage !== false) {
        toast.success(opts.successMessage ?? t("common.saved"));
      }
    },
    onError: (err) => {
      if (!opts.inlineError) {
        toast.error(err instanceof Error && err.message ? err.message : t("errors.unknown"));
      }
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  const rawMutate = mutation.mutate;
  const mutate = useCallback<typeof rawMutate>(
    (call, options) => {
      if (inFlight.current) return;
      inFlight.current = true;
      rawMutate(call, options);
    },
    [rawMutate],
  );

  return { ...mutation, mutate };
}
